import type { ProgramMembership, CustomProgram, ProgramStatus } from "@/types/program";
import { apiRequest } from "@/lib/queryClient";

const MEMBERSHIP_KEY = 'programMemberships';

export function loadProgramMemberships(): ProgramMembership[] {
  try {
    const stored = localStorage.getItem(MEMBERSHIP_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
    
    // Migration: convert old format to new format
    const myPrograms: string[] = JSON.parse(localStorage.getItem('myPrograms') || '[]');
    const favorites: string[] = JSON.parse(localStorage.getItem('favoritePrograms') || '[]');
    const customPrograms: CustomProgram[] = JSON.parse(localStorage.getItem('customPrograms') || '[]');
    const customIds = customPrograms.map(p => p.id);
    
    const memberships: ProgramMembership[] = [];
    const allIds = Array.from(new Set([...myPrograms, ...favorites]));
    
    for (const id of allIds) {
      const isCurrent = myPrograms.includes(id);
      const isFav = favorites.includes(id);
      const isCustom = customIds.includes(id);
      
      memberships.push({
        programId: id,
        status: isCurrent ? 'current' : 'favorite',
        isFavorite: isFav,
        source: isCustom ? 'custom' : 'expert',
        addedAt: new Date().toISOString()
      });
    }
    
    // Save migrated data
    if (memberships.length > 0) {
      localStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(memberships));
    }
    
    return memberships;
  } catch {
    return [];
  }
}

export function saveProgramMemberships(memberships: ProgramMembership[]) {
  localStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(memberships));
}

export function saveProgramMembershipsWithSync(
  memberships: ProgramMembership[], 
  user: any, 
  deletedProgramId?: string
) {
  localStorage.setItem(MEMBERSHIP_KEY, JSON.stringify(memberships));
  
  // Sync to backend if user is logged in
  if (user) {
    if (deletedProgramId) {
      // Delete membership from backend - don't sync after delete to avoid re-adding
      apiRequest('DELETE', `/api/user-programs/membership/${deletedProgramId}`).catch(console.error);
    } else {
      // Only sync when not deleting (adds/updates)
      apiRequest('POST', '/api/user-programs/sync', { memberships }).catch(console.error);
    }
  }
}

export function detectProgramModifications(programId: string): boolean {
  try {
    const progressKey = `programProgress_${programId}`;
    const stored = localStorage.getItem(progressKey);
    if (!stored) return false;
    
    const progress = JSON.parse(stored);
    
    const hasExerciseOverrides = progress.exerciseOverrides && Object.keys(progress.exerciseOverrides).length > 0;
    const hasSetOverrides = progress.setOverrides && Object.keys(progress.setOverrides).length > 0;
    const hasSetPlanOverrides = progress.setPlanOverrides && Object.keys(progress.setPlanOverrides).length > 0;
    const hasSkippedExercises = progress.skippedExercises && Object.keys(progress.skippedExercises).length > 0;
    const hasOrderOverrides = progress.exerciseOrderOverrides && Object.keys(progress.exerciseOrderOverrides).length > 0;
    
    return hasExerciseOverrides || hasSetOverrides || hasSetPlanOverrides || hasSkippedExercises || hasOrderOverrides;
  } catch {
    return false;
  }
}
